import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Chip,
  Button,
  useTheme,
  alpha
} from '@mui/material';
import {
  Chat as ChatIcon,
  StickyNote2 as NoteIcon,
  AlternateEmail as MentionIcon,
  Settings as SystemIcon,
  MoreVert as MoreVertIcon,
  MarkEmailRead as MarkReadIcon,
  Delete as DeleteIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { TeamNotification, NotificationType } from '../../utils/types';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import { 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  deleteNotification,
  deleteReadNotifications 
} from '../../services/teamNotificationService';
import { useNavigate } from 'react-router-dom';

interface NotificationsListProps {
  notifications: TeamNotification[];
  currentUserId: string;
}

const NotificationsList: React.FC<NotificationsListProps> = ({
  notifications,
  currentUserId
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<TeamNotification | null>(null);

  // تصنيف الإشعارات (غير المقروءة أولاً)
  const sortedNotifications = notifications.sort((a, b) => {
    if (a.isRead !== b.isRead) {
      return a.isRead ? 1 : -1;
    }
    return b.createdAt.getTime() - a.createdAt.getTime();
  });

  // الحصول على أيقونة نوع الإشعار
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'message':
        return <ChatIcon />;
      case 'note':
        return <NoteIcon />;
      case 'mention':
        return <MentionIcon />;
      case 'system':
        return <SystemIcon />;
      default:
        return <CircleIcon />;
    }
  };

  // الحصول على لون نوع الإشعار
  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'message':
        return theme.palette.primary.main;
      case 'note':
        return theme.palette.secondary.main;
      case 'mention':
        return theme.palette.warning.main;
      case 'system':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // الحصول على نص نوع الإشعار
  const getNotificationTypeText = (type: NotificationType) => {
    switch (type) {
      case 'message':
        return 'رسالة';
      case 'note':
        return 'ملاحظة';
      case 'mention':
        return 'إشارة';
      case 'system':
        return 'نظام';
      default:
        return '';
    }
  };

  // تنسيق الوقت
  const formatTime = (date: Date) => {
    try {
      return formatDistanceToNow(date, { addSuffix: true, locale: ar });
    } catch (error) {
      return 'الآن';
    }
  };

  // التعامل مع النقر على الإشعار
  const handleNotificationClick = async (notification: TeamNotification) => {
    // تمييز كمقروء إذا لم يكن مقروءاً
    if (!notification.isRead) {
      try {
        await markNotificationAsRead(notification.id);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // الانتقال إلى الصفحة المرتبطة
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  // فتح قائمة الخيارات
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, notification: TeamNotification) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  // إغلاق قائمة الخيارات
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  // تمييز إشعار واحد كمقروء
  const handleMarkAsRead = async () => {
    if (!selectedNotification) return;

    try {
      await markNotificationAsRead(selectedNotification.id);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
    
    handleMenuClose();
  };

  // حذف إشعار واحد
  const handleDeleteNotification = async () => {
    if (!selectedNotification) return;

    try {
      await deleteNotification(selectedNotification.id);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
    
    handleMenuClose();
  };

  // تمييز جميع الإشعارات كمقروءة
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(currentUserId);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // حذف جميع الإشعارات المقروءة
  const handleDeleteAllRead = async () => {
    try {
      await deleteReadNotifications(currentUserId);
    } catch (error) {
      console.error('Error deleting read notifications:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const readCount = notifications.filter(n => n.isRead).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* رأس الإشعارات */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            الإشعارات
          </Typography>
          
          {unreadCount > 0 && (
            <Chip
              size="small"
              label={`${unreadCount} جديد`}
              color="error"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>

        {/* أزرار الإجراءات */}
        {notifications.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MarkReadIcon />}
                onClick={handleMarkAllAsRead}
                sx={{ fontSize: '0.75rem' }}
              >
                تمييز الكل كمقروء
              </Button>
            )}
            
            {readCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteAllRead}
                sx={{ fontSize: '0.75rem' }}
              >
                حذف المقروءة
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* قائمة الإشعارات */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {sortedNotifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              لا توجد إشعارات
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {sortedNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 2,
                      px: 2,
                      bgcolor: notification.isRead ? 'transparent' : alpha(theme.palette.primary.main, 0.05),
                      borderRight: notification.isRead ? 'none' : `3px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                      }
                    }}
                  >
                    {/* أيقونة نوع الإشعار */}
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: getNotificationColor(notification.type),
                          fontSize: '1rem'
                        }}
                      >
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemIcon>

                    {/* محتوى الإشعار */}
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={notification.isRead ? 'medium' : 'bold'}
                            sx={{
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {notification.title}
                          </Typography>

                          {/* نوع الإشعار */}
                          <Chip
                            size="small"
                            label={getNotificationTypeText(notification.type)}
                            sx={{
                              height: 18,
                              fontSize: '0.7rem',
                              bgcolor: alpha(getNotificationColor(notification.type), 0.1),
                              color: getNotificationColor(notification.type)
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          {/* محتوى الإشعار */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mb: 0.5
                            }}
                          >
                            {notification.content}
                          </Typography>

                          {/* الوقت */}
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(notification.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />

                    {/* مؤشر عدم القراءة */}
                    {!notification.isRead && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        <CircleIcon 
                          sx={{ 
                            fontSize: 8, 
                            color: theme.palette.primary.main 
                          }} 
                        />
                      </Box>
                    )}

                    {/* زر الخيارات */}
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, notification)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
                
                {index < sortedNotifications.length - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* قائمة الخيارات */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {selectedNotification && !selectedNotification.isRead && (
          <MenuItem onClick={handleMarkAsRead}>
            <MarkReadIcon sx={{ mr: 1, fontSize: 18 }} />
            <Typography variant="body2">تمييز كمقروء</Typography>
          </MenuItem>
        )}
        
        <MenuItem onClick={handleDeleteNotification} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">حذف</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NotificationsList;
